import type { ChartTable } from "@coval/ui/chart-figure"

import type { CovalChartSpecV1 } from "../model/chart/types"
import { formatChartValue } from "./chartSpec"

/** Only bar and area charts stack to 100%; a line chart ignores the option. */
function isPercentStacked(spec: CovalChartSpecV1): boolean {
  return (
    spec.opts?.percent_stack === true &&
    (spec.chart === "bar" || spec.chart === "area")
  )
}

/**
 * The value format the chart's axis and tooltip use: `percent_stack` reads
 * values as fractions on every chart type, and only bar and area also stack.
 */
function valueFormatOf(spec: CovalChartSpecV1) {
  return spec.opts?.percent_stack === true && !isPercentStacked(spec)
    ? "percent"
    : spec.y?.format
}

/** Any nonzero row total divides, as the chart's expand offset does. */
function share(value: number, total: number): string {
  return total !== 0 ? `${((value / total) * 100).toFixed(1)}%` : "—"
}

function formatX(spec: CovalChartSpecV1, value: unknown): string {
  return formatChartValue(value, spec.x?.kind === "time" ? "date" : undefined)
}

/** The spec's rows as a table: the x field, then one column per series. */
export function chartSpecTable(spec: CovalChartSpecV1): ChartTable {
  // A percent-stacked chart scales each row to 100%, so each cell shows that
  // share with the row's value beside it.
  const format = valueFormatOf(spec)
  const percentStack = isPercentStacked(spec)
  return {
    columns: [
      ...(spec.x ? [{ key: "x", header: spec.x.label ?? spec.x.field }] : []),
      ...spec.series.map((series, index) => ({
        key: `series-${index}`,
        header: series.name ?? series.field,
        numeric: true,
      })),
    ],
    rows: spec.data.map((row, rowIndex) => {
      const rowTotal = spec.series.reduce((sum, series) => {
        const value = row[series.field]
        return typeof value === "number" && Number.isFinite(value)
          ? sum + value
          : sum
      }, 0)
      return {
        key: String(rowIndex),
        cells: {
          ...(spec.x ? { x: formatX(spec, row[spec.x.field]) || "—" } : {}),
          ...Object.fromEntries(
            spec.series.map((series, index) => {
              const raw = row[series.field]
              const text = formatChartValue(raw, format) || "—"
              return [
                `series-${index}`,
                percentStack && typeof raw === "number" && Number.isFinite(raw)
                  ? `${share(raw, rowTotal)} (${text})`
                  : text,
              ]
            }),
          ),
        },
      }
    }),
  }
}

/**
 * The extremes a sighted reader takes from the chart: for each series, its
 * highest and lowest row.
 */
export function chartSpecSummary(spec: CovalChartSpecV1): string | undefined {
  if (spec.data.length === 0 || !spec.x) {
    return undefined
  }
  const xField = spec.x.field
  const format = valueFormatOf(spec)
  const parts = spec.series.flatMap((series) => {
    const points = spec.data.flatMap((row) => {
      const value = row[series.field]
      return typeof value === "number" && Number.isFinite(value)
        ? [{ value, at: formatX(spec, row[xField]) }]
        : []
    })
    if (points.length === 0) {
      return []
    }
    const high = points.reduce((top, point) =>
      point.value > top.value ? point : top,
    )
    const low = points.reduce((bottom, point) =>
      point.value < bottom.value ? point : bottom,
    )
    const name = series.name ?? series.field
    return points.length === 1
      ? [`${name}: ${formatChartValue(high.value, format)} at ${high.at}.`]
      : [
          `${name}: highest ${formatChartValue(high.value, format)} at ${high.at}, lowest ${formatChartValue(low.value, format)} at ${low.at}.`,
        ]
  })
  const rows = `${spec.data.length} row${spec.data.length === 1 ? "" : "s"}.`
  const stackNote = isPercentStacked(spec)
    ? "The chart stacks each row to 100%; the table shows each series' share with its value in brackets."
    : undefined
  return [rows, ...parts, stackNote].filter(Boolean).join(" ")
}
